import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { normalizeFileDetailFilters } from "lib/utils/fileDetailFilters";
import { getProjectNodes } from "lib/api/projectStructure";
import { projectNodePaths, projectPathLabel } from "lib/utils/projectNodePaths";

// Keep unapplied edits separate from the filters used by previews and exports.
export default function ProjectReportDetailConditions({ projectId, groups, value, appliedScope, onApply, onDraftChange, disabled, t }) {
  const [draft, setDraft] = useState(() => normalizeFileDetailFilters(value || {}));
  const [nodeId, setNodeId] = useState(() => appliedScope?.treeNodeId || "");
  const [tree, setTree] = useState({ loading: true, nodes: [], error: "" });
  const [retry, setRetry] = useState(0);
  const [pathOpen, setPathOpen] = useState(false);
  const pathPickerRef = useRef(null);
  const pathButtonRef = useRef(null);
  const pathOptionsId = useId();
  const paths = useMemo(() => projectNodePaths(tree.nodes), [tree.nodes]);
  const selectedPath = paths.find(path => path.id === nodeId);
  const pathDisabled = disabled || tree.loading || Boolean(tree.error);
  useEffect(() => {
    if (pathDisabled) setPathOpen(false);
  }, [pathDisabled]);
  useEffect(() => {
    if (!pathOpen) return;
    const closeOutside = event => {
      if (!pathPickerRef.current?.contains(event.target)) setPathOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [pathOpen]);
  const hasFilters = Object.values(draft).some(items => items.length);
  const selection = { ...draft, all: hasFilters ? [] : ["all"] };
  const applied = Boolean(value) && nodeId === (appliedScope?.treeNodeId || "")
    && JSON.stringify(draft) === JSON.stringify(normalizeFileDetailFilters(value));
  useEffect(() => {
    const controller = new AbortController();
    setTree({ loading: true, nodes: [], error: "" });
    getProjectNodes(projectId, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      setTree({ loading: false, nodes: result.success ? result.data.nodes : [],
        error: result.success ? "" : result.message || "경로를 불러오지 못했습니다." });
    }).catch(() => {
      if (!controller.signal.aborted) setTree({ loading: false, nodes: [], error: "경로를 불러오지 못했습니다." });
    });
    return () => controller.abort();
  }, [projectId, retry]);
  const toggle = (key, option) => {
    onDraftChange?.();
    setDraft(previous => ({ ...previous,
      [key]: previous[key].includes(option) ? previous[key].filter(item => item !== option) : [...previous[key], option],
    }));
  };
  return <div className="projectReportDetailConditions">
    <div className="projectReportDetailConditions__scope">
      <div className="projectReportDetailConditions__pathPicker" ref={pathPickerRef}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setPathOpen(false); }}
        onKeyDown={event => {
          if (event.key === "Escape" && pathOpen) {
            event.preventDefault(); event.stopPropagation(); setPathOpen(false); pathButtonRef.current?.focus();
          }
        }}>
        <span id={`${pathOptionsId}-label`}>출력 경로</span>
        <button type="button" ref={pathButtonRef} className="projectReportDetailConditions__pathButton"
          disabled={pathDisabled} aria-expanded={pathOpen} aria-controls={pathOptionsId}
          aria-labelledby={`${pathOptionsId}-label ${pathOptionsId}-value`}
          onClick={() => setPathOpen(previous => !previous)}>
          <span id={`${pathOptionsId}-value`}>{selectedPath ? projectPathLabel(selectedPath) : "프로젝트 전체"}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        {pathOpen && <div id={pathOptionsId} className="projectReportDetailConditions__pathOptions" role="group" aria-label="출력 경로">
          {[{ id: "", label: "프로젝트 전체" }, ...paths.map(path => ({ id: path.id, label: projectPathLabel(path) }))].map(option =>
            <button type="button" key={option.id} className={nodeId === option.id ? "is-selected" : ""}
              aria-pressed={nodeId === option.id} disabled={pathDisabled}
              onClick={() => {
                if (nodeId !== option.id) { setNodeId(option.id); onDraftChange?.(); }
                setPathOpen(false); pathButtonRef.current?.focus();
              }}>
              <span>{option.label}</span>
            </button>)}
        </div>}
      </div>
      <p>선택한 경로와 모든 하위 경로의 파일을 포함합니다.{groups.length > 0 ? " 아래 조건은 이 범위 안에서 추가로 적용됩니다." : ""}</p>
      {tree.loading && <p role="status">경로를 불러오는 중…</p>}
      {tree.error && <div role="alert"><p>{tree.error}</p><button type="button" className="projectReportEditorAction" disabled={disabled} onClick={() => setRetry(value => value + 1)}>경로 다시 불러오기</button></div>}
    </div>
    {groups.length > 0 && <div className="projectReportDetailConditions__groups">
      {groups.filter(group => group.key !== "all").map(group => <fieldset key={group.key} disabled={disabled}>
        <legend>{group.label}</legend>
        <div>{group.options.map(option => <label key={option.value} className={`projectReportEditorSelectionOption ${draft[group.key].includes(String(option.value)) ? "is-active" : ""}`}>
          <input type="checkbox" checked={draft[group.key].includes(String(option.value))} onChange={() => toggle(group.key, String(option.value))} />
          <span>{option.label}</span>
        </label>)}</div>
      </fieldset>)}
      <p>여러 항목을 선택할 수 있습니다. 선택하지 않은 분류는 전체를 포함합니다.</p>
    </div>}
    <div className="projectReportDetailConditions__actions">
      <span role="status">{applied ? "적용된 조건입니다." : "불러오기를 누르면 선택한 조건으로 미리보기를 갱신합니다."}</span>
      <button type="button" className="projectReportEditorAction is-primary" disabled={disabled || Boolean(nodeId && (tree.loading || tree.error || !paths.some(path => path.id === nodeId)))} onClick={() => onApply(selection, nodeId ? { treeNodeId: nodeId } : undefined)}>
        {t("projectDetail.projectReportEditor.filter.apply", { defaultValue: "불러오기" })}
      </button>
    </div>
  </div>;
}
